import { Link } from 'react-router-dom';

import { useAuthConfig } from '../lib/auth/authConfig';
import { t } from '../lib/i18n';

/**
 * Footer público reusable — landing y páginas públicas (PublicPageLayout).
 *
 * Estructura: marca + tagline, y tres columnas de enlaces (Producto / Empresa /
 * Legal). Empresa y Legal navegan a páginas SPA con <Link> (respetan el
 * basename "/tembiapo"). Producto apunta a las anclas de la home; usa el
 * BASE_URL de Vite para que funcione desde cualquier página (navega a la home y
 * hace scroll al ancla). Colores 100% desde tokens.
 */

// BASE_URL de Vite = "/tembiapo/". Los enlaces de anclas de la home apuntan
// acá para funcionar tanto en la landing como en las páginas legales.
const BASE = import.meta.env.BASE_URL;

function BrandMark() {
  return <img src={`${BASE}brand/logo-lockup.png`} alt="Tembiapo" className="h-8 w-auto" />;
}

type AnchorLink = { label: string; href: string };
type RouteLink = { label: string; to: string };

function AnchorCol({ title, links }: { title: string; links: AnchorLink[] }) {
  return (
    <div>
      <h5 className="mb-3.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">{title}</h5>
      <ul className="flex flex-col gap-2 text-sm text-muted">
        {links.map((l) => (
          <li key={l.href}>
            <a href={l.href} className="transition-colors hover:text-brand-2">
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RouteCol({ title, links }: { title: string; links: RouteLink[] }) {
  return (
    <div>
      <h5 className="mb-3.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">{title}</h5>
      <ul className="flex flex-col gap-2 text-sm text-muted">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="transition-colors hover:text-brand-2">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PublicFooter() {
  // El sello de Bancard sólo se muestra si el pago con tarjeta está ENCENDIDO en
  // el panel Super Admin (card_enabled del /auth/config público).
  const authConfig = useAuthConfig();
  const showBancard = authConfig.data?.card_enabled === true;

  return (
    <footer className="border-t border-line pb-8 pt-12">
      <div className="mx-auto w-full max-w-6xl px-6">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
          <div>
            <BrandMark />
            <p className="mt-3 max-w-[32ch] text-sm text-muted">{t('landing.footer.tagline')}</p>
          </div>

          <AnchorCol
            title={t('landing.footer.product')}
            links={[
              { label: t('landing.footer.link.features'), href: `${BASE}#features` },
              { label: t('landing.footer.link.plans'), href: `${BASE}#plans` },
              { label: t('landing.footer.link.apps'), href: `${BASE}#apps` },
            ]}
          />

          <RouteCol
            title={t('landing.footer.company')}
            links={[
              { label: t('landing.footer.link.about'), to: '/sobre-nosotros' },
              { label: t('landing.footer.link.contact'), to: '/contacto' },
              { label: t('help.nav.help'), to: '/ayuda' },
              { label: t('landing.footer.link.support'), to: '/soporte' },
            ]}
          />

          <RouteCol
            title={t('landing.footer.legal')}
            links={[
              { label: t('landing.footer.link.privacy'), to: '/privacidad' },
              { label: t('landing.footer.link.terms'), to: '/terminos' },
              { label: t('landing.footer.link.cookies'), to: '/cookies' },
            ]}
          />
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-5 text-xs text-muted">
          <span>{t('landing.footer.copyright')}</span>
          {showBancard && (
            <span className="inline-flex items-center gap-2">
              <span>{t('landing.footer.bancard')}</span>
              <img
                src={`${BASE}brand/bancard.png`}
                alt="Pago seguro Bancard — Visa, Mastercard, Amex, Diners"
                className="h-7 w-auto rounded bg-white px-2 py-1 ring-1 ring-line"
                loading="lazy"
              />
            </span>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{t('landing.footer.madeIn')}</span>
            <a
              href="https://evoluado.com"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-brand-2"
            >
              {t('common.poweredBy')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
